import { useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { Eye, EyeOff, User, Lock, Loader2 } from 'lucide-react'
import { loginWithUsername } from '@/lib/auth'
import { Input } from '@/components/ui/input'
import { toast } from '@/lib/toast'
import { PartnerLogosCarousel } from '@/components/PartnerLogosCarousel'

export function LoginPage() {
  const navigate = useNavigate()
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [agreed, setAgreed] = useState(false)
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState<{
    username?: string
    password?: string
    form?: string
  }>({})

  const isFormValid =
    username.trim().length >= 4 && password.length > 0 && agreed

  async function handleLogin(e: React.FormEvent) {
    e.preventDefault()
    setErrors({})

    const newErrors: typeof errors = {}
    if (!username.trim()) newErrors.username = 'Username is required'
    else if (username.trim().length < 4)
      newErrors.username = 'Username must be at least 4 characters'
    if (!password) newErrors.password = 'Password is required'
    if (!agreed)
      newErrors.form = 'You must agree to the Terms and Privacy Policy'

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      return
    }

    setLoading(true)
    try {
      const { error } = await loginWithUsername(
        username.toLowerCase().trim(),
        password
      )

      if (error) {
        setErrors({ form: 'Incorrect username or password' })
        setLoading(false)
        return
      }

      toast.success('Welcome back!')
      navigate('/', { replace: true })
    } catch {
      setErrors({ form: 'Something went wrong. Please try again.' })
      setLoading(false)
    }
  }

  function handleForgotPassword() {
    toast.info(
      'Password recovery coming soon. For now, contact your barangay admin to reset.'
    )
  }

  return (
    <div className="space-y-4">
      <p className="text-center text-sm text-muted-foreground">
        Connecting your barangay community
      </p>

      <form onSubmit={handleLogin} className="space-y-3.5">
        {/* Form-level error */}
        {errors.form && (
          <div className="text-center text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-4 py-2.5">
            {errors.form}
          </div>
        )}

        {/* Username */}
        <div className="space-y-1">
          <div className="relative">
            <User className="absolute left-3.5 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground pointer-events-none" />
            <Input
              type="text"
              inputMode="text"
              autoComplete="username"
              autoCapitalize="none"
              autoCorrect="off"
              placeholder="Username"
              value={username}
              onChange={(e) => setUsername(e.target.value.toLowerCase())}
              className={`pl-11 h-11 rounded-full border-2 ${
                errors.username
                  ? 'border-red-500'
                  : 'border-input focus:border-red-500'
              }`}
              disabled={loading}
            />
          </div>
          {errors.username && (
            <p className="text-xs text-red-600 ml-4">{errors.username}</p>
          )}
        </div>

        {/* Password */}
        <div className="space-y-1">
          <div className="relative">
            <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground pointer-events-none" />
            <Input
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={`pl-11 pr-11 h-11 rounded-full border-2 ${
                errors.password
                  ? 'border-red-500'
                  : 'border-input focus:border-red-500'
              }`}
              disabled={loading}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              tabIndex={-1}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? (
                <EyeOff className="h-5 w-5" />
              ) : (
                <Eye className="h-5 w-5" />
              )}
            </button>
          </div>
          {errors.password && (
            <p className="text-xs text-red-600 ml-4">{errors.password}</p>
          )}
        </div>

        {/* Register / Forgot row */}
        <div className="flex justify-between text-sm font-medium px-1">
          <Link
            to="/register"
            className="text-foreground hover:text-red-600 transition-colors"
          >
            Register Now
          </Link>
          <button
            type="button"
            onClick={handleForgotPassword}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            Forgot Password?
          </button>
        </div>

        {/* Agreement */}
        <label className="flex items-start gap-2.5 rounded-2xl border-2 border-input px-4 py-2.5 cursor-pointer">
          <input
            type="checkbox"
            checked={agreed}
            onChange={(e) => setAgreed(e.target.checked)}
            disabled={loading}
            className="mt-0.5 h-4 w-4 rounded border-red-400 text-red-600 accent-red-600"
          />
          <span className="text-xs text-muted-foreground leading-relaxed flex-1">
            I agree to kemyoon's{' '}
            <Link
              to="/privacy"
              className="text-red-600 font-semibold hover:underline"
            >
              Privacy Policy
            </Link>{' '}
            and{' '}
            <Link
              to="/terms"
              className="text-red-600 font-semibold hover:underline"
            >
              Terms of Use
            </Link>
            .
          </span>
        </label>

        {/* Login button — solid red brand color */}
        <button
          type="submit"
          disabled={!isFormValid || loading}
          className="w-full h-11 text-base font-semibold rounded-full bg-red-600 hover:bg-red-700 text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
        >
          {loading ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Signing in...
            </>
          ) : (
            'LOGIN'
          )}
        </button>
      </form>

      {/* Partner logos carousel */}
      <div className="pt-4">
        <PartnerLogosCarousel />
      </div>
    </div>
  )
}
